/**
 * The dashboard sidebar's collapsed state.
 *
 * It is a per-browser preference in localStorage, so the server cannot know it.
 * If React state decided the layout, every reload would paint the expanded
 * sidebar and then collapse it once effects run. Instead the state is mirrored
 * onto `<html data-sidebar="rail">` and the layout differences are CSS (the
 * `sidebar-rail:` variant in styles.css), so the first frame is already right:
 * `SIDEBAR_INIT_SCRIPT` sets the attribute while the HTML is still parsing.
 */

export const SIDEBAR_PIN_STORAGE_KEY = "bavimail.dashboard.sidebar-pinned";

// Runs inline before paint. No user-controlled value reaches the document: the
// stored string is only compared, never written.
export const SIDEBAR_INIT_SCRIPT = `(() => {
  try {
    if (localStorage.getItem('${SIDEBAR_PIN_STORAGE_KEY}') === 'false') {
      document.documentElement.dataset.sidebar = 'rail';
    }
  } catch {}
})();`;

export function readSidebarPinned(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_PIN_STORAGE_KEY) !== "false";
  } catch {
    // Storage blocked: fall back to whatever the document already shows.
    return document.documentElement.dataset.sidebar !== "rail";
  }
}

export function applySidebarPinned(pinned: boolean) {
  if (pinned) delete document.documentElement.dataset.sidebar;
  else document.documentElement.dataset.sidebar = "rail";
}

export function writeSidebarPinned(pinned: boolean) {
  applySidebarPinned(pinned);
  try {
    window.localStorage.setItem(SIDEBAR_PIN_STORAGE_KEY, pinned ? "true" : "false");
  } catch {
    // The toggle still works for this page session when persistence is blocked.
  }
}
